import { useAppSelector, useCart } from '@zonofi/zono-money-helpers';
import {
  ProductDetails,
  ProductFacetMap,
  getCustomerInvite,
  useAddItemToActiveOrderMutation,
  useEditOrderMutation,
  useGetOrderDetailsMutation,
  useGetProductsByCustomerMutation,
  useGetTrackPODetailsMutation,
} from '@zonofi/zono-money-store';
import { isUndefined } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

export const useProductDetailsV1 = () => {
  const [searchParams] = useSearchParams();
  const fromSource = searchParams.get('source');
  const sourceOrderId = searchParams.get('orderId');
  const pvId = searchParams.get('pvId');
  const campaignId = searchParams.get('campaignId');
  const hideImages = searchParams.get('hImg') === 'true';
  const hidePrice = searchParams?.get('hp') === 'true';

  const inviteData = useAppSelector(getCustomerInvite);
  const workspaceId = inviteData?.seller?.workspaceId;
  const customerId = inviteData?.inviteId;

  const params = useParams();
  const { productId } = params;

  const {
    callCartAPI,
    cartAPIResponse,
    productsInCart,
    setProductsInCart,
    cartSummaryAPIResponse,
    onIncrement,
    onDecrement,
    onDelete,
    onChangeQuantity,
    editOrderResponse,
  } = useCart();

  const [error, setError] = useState({
    show: false,
    msg: '',
  });
  const [productDetails, setProductDetails] = useState<ProductDetails>({
    title: '',
    price: 0,
    tags: [],
    description: '',
    details: {
      ptr: '',
      margin: '',
      moq: 0,
    },
    promotions: [],
    images: [],
    itemInCart: undefined,
    productVariantId: 0,
    facets: [],
    segments: [],
    stock: 0,
  });
  const [addedToCart, setAddedToCart] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [isMoqError, setIsMoqError] = useState(false);
  const [facets, setFacets] = useState<ProductFacetMap>({});
  const [activeProductVariantId, setActiveProductVariantId] = useState<number>(
    Number(pvId)
  );

  const isPoFile = useMemo(() => {
    return isNaN(Number(sourceOrderId));
  }, [sourceOrderId]);

  const [getProductDetails, getProductDetailsResponse] =
    useGetProductsByCustomerMutation();
  const [fetchOrderDetailsAPI, fetchOrderDetailAPIResponse] =
    useGetOrderDetailsMutation();
  const [fetchPoFileDetailsAPI, fetchPoFileDetailsAPIResponse] =
    useGetTrackPODetailsMutation();
  const [addItemToSubmittedOrderAPI, addItemToSubmittedOrderAPIResponse] =
    useEditOrderMutation();
  const [addToCartAPI, addToCartAPIResponse] =
    useAddItemToActiveOrderMutation();
  const allProductVariants =
    getProductDetailsResponse?.data?.products?.[0]?.productVariants ?? [];

  const onSelectFacet = (facetCode, facetValue) => {
    console.log('facetCode, facetValue: ', facetCode, facetValue);
    console.log('updating selected facet ');
    setFacets((f) => {
      return {
        ...f,
        [facetCode]: {
          ...f?.[facetCode],
          facetValues: f?.[facetCode]?.facetValues?.map((i) => {
            if (i?.facetValue === facetValue) {
              return {
                ...i,
                isActive: true,
              };
            }

            return { ...i, isActive: false };
          }),
        },
      };
    });
  };

  const onAddToCart = (item: any) => {
    if (fromSource === 'order' && sourceOrderId) {
      let orderId = sourceOrderId;
      if (isPoFile) {
        orderId = fetchPoFileDetailsAPIResponse?.data?.lines
          ?.find((i) => i?.orderId)
          ?.orderId?.toString();
      }
      addItemToSubmittedOrderAPI({
        workspaceId,
        body: {
          customerId,
          lines: [
            {
              quantity: 1,
              productVariantId: item?.productVariantId,
              operator: 'add',
            },
          ],
          orderId: orderId,
        },
      });
    } else {
      addToCartAPI({
        sellerWorkspaceId: workspaceId,
        customerId,
        source: 'platform',
        ...(campaignId ? { campaignId } : {}),
        ...(sourceOrderId ? { orderId: sourceOrderId } : {}),
        lines: [
          {
            productVariantId: item?.productVariantId,
            quantity: item?.minOrderQty,
            operator: 'add',
          },
        ],
      });
    }
  };

  const isLoading = useMemo(() => {
    return (
      addToCartAPIResponse?.isLoading ||
      editOrderResponse?.isLoading ||
      addItemToSubmittedOrderAPIResponse?.isLoading
    );
  }, [
    addToCartAPIResponse,
    editOrderResponse,
    addItemToSubmittedOrderAPIResponse,
  ]);

  useEffect(() => {
    if (editOrderResponse.isError) {
      setIsMoqError(true);
      setTimeout(() => {
        setIsMoqError(false);
      }, 2000);
    }
  }, [editOrderResponse]);

  useEffect(() => {
    if (productId || activeProductVariantId) {
      getProductDetails({
        workspaceId: workspaceId,
        params: {
          customerId,
          sellerWorkspaceId: workspaceId,
        },
        body: {
          includeFacets: true,
          includeDivisions: false,
          includeCfas: false,
          ...(productId
            ? { productIds: [productId], groupByProduct: true }
            : { productVariantId: Number(activeProductVariantId) }),
        },
      });
    }
  }, [productId]);

  useEffect(() => {
    if (!productId && activeProductVariantId) {
      getProductDetails({
        workspaceId: workspaceId,
        params: {
          customerId,
          sellerWorkspaceId: workspaceId,
        },
        body: {
          includeFacets: true,
          includeDivisions: false,
          includeCfas: false,
          productVariantId: Number(activeProductVariantId),
        },
      });
    }
  }, [activeProductVariantId]);

  useEffect(() => {
    if (getProductDetailsResponse?.isSuccess) {
      let activeProduct =
        getProductDetailsResponse?.data?.products[0]?.productVariants[0];

      if (!isUndefined(activeProductVariantId)) {
        activeProduct =
          getProductDetailsResponse?.data?.products[0]?.productVariants?.filter(
            (i) => i?.productVariantId === activeProductVariantId
          )?.[0];
      }

      setProductDetails({
        title: activeProduct?.name,
        price: activeProduct?.basePrice || 0,
        tags: activeProduct?.categories || [],
        description: activeProduct?.shortDescription,
        promotions: activeProduct?.promotions,
        details: {
          ptr: `${activeProduct?.PTR?.toFixed(2)}`,
          margin: `${(activeProduct?.PTR - activeProduct?.price)?.toFixed(2)}`,
          moq: activeProduct?.minOrderQty,
          weightedPrice: activeProduct?.price,
          pricingStrategy: activeProduct?.pricingStrategy,
        },
        // images: activeProduct?.assets?.map((i) => i?.redirectUrl),
        images:
          activeProduct?.assets?.map((asset) => asset?.redirectUrl || '') || [],
        productVariantId: activeProduct?.productVariantId,
        facets: activeProduct?.facets,
        segments: activeProduct?.segments,
        stock: activeProduct?.stock ?? 0,
      });

      if (fromSource === 'order') {
        if (isPoFile) {
          fetchPoFileDetailsAPI({
            workspaceId,
            body: {
              pofileId: sourceOrderId,
              customerId,
              includePromotionData: false,
              includeInvoice: false,
              includePromotion: false,
              includeTax: false,
            },
          });
        } else {
          fetchOrderDetailsAPI({
            workspaceId: workspaceId,
            orderId: Number(sourceOrderId),
            body: {
              includeInvoice: false,
              includeTax: false,
              includeCustomer: false,
              includePromotions: false,
              customerId,
              includePromotionData: false,
            },
          });
        }
      } else {
        callCartAPI({
          customerId,
          workspaceId,
          sellerWorkspaceId: workspaceId,
        });
      }
    } else if (getProductDetailsResponse?.isError) {
      setError({
        show: true,
        msg: 'Something went wrong!',
      });
    }
  }, [getProductDetailsResponse, activeProductVariantId]);

  useEffect(() => {
    if (getProductDetailsResponse?.isSuccess) {
      let activeProduct =
        getProductDetailsResponse?.data?.products[0]?.productVariants[1];

      if (!isUndefined(activeProductVariantId)) {
        activeProduct =
          getProductDetailsResponse?.data?.products[0]?.productVariants?.filter(
            (i) => i?.productVariantId === activeProductVariantId
          )?.[0];
      }

      let tempFacets: ProductFacetMap = {};

      getProductDetailsResponse?.data?.products?.[0]?.productVariants?.forEach(
        (variant) => {
          variant?.facets?.forEach((f) => {
            // Skip null/undefined facet values
            if (!f?.values?.[0]) return;

            let value = {
              ...f?.values?.[0],
              isActive: false,
            };

            let facetExistingInCurrentProduct = activeProduct?.facets?.filter(
              (i) => i?.facetCode === f?.facetCode
            )?.[0];

            if (
              facetExistingInCurrentProduct &&
              facetExistingInCurrentProduct?.values?.[0] === f?.values?.[0]
            ) {
              value = {
                ...f?.values?.[0],
                isActive: true,
              };
            }

            const existingFacet = tempFacets[f?.facetCode] || {
              facetCode: f?.facetCode,
              facetName: f?.name || '',
              facetValues: [],
            };

            // Avoid duplicates based on `facetValueCode`
            const alreadyExists = existingFacet.facetValues.some(
              (v) => v?.facetValueCode === value?.facetValueCode
            );

            if (!alreadyExists) {
              existingFacet.facetValues.push(value);
            }

            tempFacets = {
              ...tempFacets,
              [f?.facetCode]: existingFacet,
            };
          });
        }
      );

      setFacets(tempFacets);
    }
  }, [getProductDetailsResponse]);

  useEffect(() => {
    if (cartAPIResponse?.isSuccess) {
      const productInCart = productsInCart?.find((i) => {
        return i?.productVariantId === Number(activeProductVariantId || pvId);
      });

      setProductDetails((prevState) => {
        return {
          ...prevState,
          itemInCart: {
            orderId: productInCart?.orderId,
            quantity: productInCart?.quantity,
          },
        };
      });
    }
  }, [productsInCart, cartAPIResponse]);

  useEffect(() => {
    if (
      addToCartAPIResponse?.isSuccess ||
      addItemToSubmittedOrderAPIResponse?.isSuccess
    ) {
      if (fromSource === 'order') {
        if (isPoFile) {
          fetchPoFileDetailsAPI({
            workspaceId,
            body: {
              pofileId: sourceOrderId,
              customerId,
              includePromotionData: false,
              includeInvoice: false,
              includePromotion: false,
              includeTax: false,
            },
          });
        } else {
          fetchOrderDetailsAPI({
            workspaceId: workspaceId,
            orderId: Number(sourceOrderId),
            body: {
              includeInvoice: false,
              includeTax: false,
              includeCustomer: false,
              includePromotions: false,
              customerId,
              includePromotionData: false,
            },
          });
        }
      } else {
        callCartAPI({
          customerId,
          workspaceId,
          sellerWorkspaceId: workspaceId,
        });
      }
    }
  }, [addToCartAPIResponse, addItemToSubmittedOrderAPIResponse]);

  useEffect(() => {
    if (editOrderResponse?.isSuccess) {
      if (fromSource === 'order') {
        if (isPoFile) {
          fetchPoFileDetailsAPI({
            workspaceId,
            body: {
              pofileId: sourceOrderId,
              customerId,
              includePromotionData: false,
              includeInvoice: false,
              includePromotion: false,
              includeTax: false,
            },
          });
        } else {
          fetchOrderDetailsAPI({
            workspaceId: workspaceId,
            orderId: Number(sourceOrderId),
            body: {
              includeInvoice: false,
              includeTax: false,
              includeCustomer: false,
              includePromotions: false,
              customerId,
              includePromotionData: false,
            },
          });
        }
      } else {
        callCartAPI({
          customerId,
          workspaceId,
          sellerWorkspaceId: workspaceId,
        });
      }
    }
  }, [editOrderResponse]);

  useEffect(() => {
    if (fetchOrderDetailAPIResponse?.isSuccess) {
      const productInOrder = fetchOrderDetailAPIResponse?.data?.lines?.find(
        (i) => {
          return (
            i?.productVariant?.id === Number(activeProductVariantId || pvId)
          );
        }
      );

      setProductDetails((prevState) => {
        return {
          ...prevState,
          itemInCart: {
            orderId: productInOrder?.orderId,
            quantity: productInOrder?.quantity,
          },
        };
      });
    }
  }, [fetchOrderDetailAPIResponse]);

  useEffect(() => {
    if (fetchPoFileDetailsAPIResponse?.isSuccess) {
      const productInOrder = fetchPoFileDetailsAPIResponse?.data?.lines?.find(
        (i) => {
          return i?.productVariantId === Number(activeProductVariantId || pvId);
        }
      );

      setProductDetails((prevState) => {
        return {
          ...prevState,
          itemInCart: {
            orderId: productInOrder?.orderId,
            quantity: productInOrder?.quantity,
          },
        };
      });
    }
  }, [fetchPoFileDetailsAPIResponse]);

  useEffect(() => {
    if (addToCartAPIResponse?.isSuccess) {
      setAddedToCart(true);
      setTimeout(() => {
        setAddedToCart(false);
      }, 2000);
    }
  }, [addToCartAPIResponse]);

  useEffect(() => {
    if (Object.keys(facets)?.length) {
      type ActiveFacet = { facetCode: string; facetValueCode: string };

      const activeFacetFilters: ActiveFacet[] = [];

      Object.values(facets).forEach((facet) => {
        facet.facetValues.forEach((value: any) => {
          if (value?.isActive) {
            activeFacetFilters.push({
              facetCode: facet.facetCode,
              facetValueCode: value.facetValueCode,
            });
          }
        });
      });
      console.log('activeFacetFilters: ', activeFacetFilters);

      let allVariants =
        getProductDetailsResponse?.data?.products?.[0]?.productVariants;

      const filteredVariant = allVariants.filter((variant) => {
        if (!variant?.facets) return false;

        return activeFacetFilters.every((active) =>
          variant.facets.some(
            (vf) =>
              vf.facetCode === active.facetCode &&
              vf.values?.some(
                (v: any) => v.facetValueCode === active.facetValueCode
              )
          )
        );
      })?.[0];

      console.log('filteredVariant: ', filteredVariant);

      if (filteredVariant) {
        setActiveProductVariantId(filteredVariant?.productVariantId);
      }
    }
  }, [facets]);

  useEffect(() => {
    if (pvId) {
      setActiveProductVariantId(Number(pvId));
    }
  }, [pvId]);

  const outOfStockFacetValueMap = useMemo(() => {
    if (!Object.keys(facets)?.length || !allProductVariants?.length) {
      return {};
    }

    const activeFacetFilters: Record<string, string> = {};

    Object.values(facets).forEach((facet) => {
      const activeValue = facet?.facetValues?.find(
        (value: any) => value?.isActive
      );
      if (activeValue?.facetValueCode) {
        activeFacetFilters[facet.facetCode] = activeValue.facetValueCode;
      }
    });

    const facetStockState: Record<string, Record<string, boolean>> = {};

    Object.values(facets).forEach((facet) => {
      const facetCode = facet.facetCode;
      facetStockState[facetCode] = {};

      facet.facetValues.forEach((facetValue: any) => {
        const facetValueCode = facetValue?.facetValueCode;
        if (!facetValueCode) return;

        const candidateSelection = {
          ...activeFacetFilters,
          [facetCode]: facetValueCode,
        };

        const matchingVariants = allProductVariants.filter((variant) => {
          if (!variant?.facets?.length) return false;

          return Object.entries(candidateSelection).every(
            ([selectionFacetCode, selectionFacetValueCode]) =>
              variant.facets.some(
                (variantFacet) =>
                  variantFacet.facetCode === selectionFacetCode &&
                  variantFacet.values?.some(
                    (variantFacetValue: any) =>
                      variantFacetValue?.facetValueCode ===
                      selectionFacetValueCode
                  )
              )
          );
        });

        const hasInStockVariant = matchingVariants.some(
          (variant) => Number(variant?.stock ?? 0) > 0
        );

        facetStockState[facetCode][facetValueCode] =
          matchingVariants.length > 0 && !hasInStockVariant;
      });
    });

    return facetStockState;
  }, [facets, allProductVariants]);

  return {
    productDetails,
    getProductDetailsResponse,
    onAddToCart,
    onIncrement,
    onDecrement,
    onDelete,
    onChangeQuantity,
    error,
    isLoading,
    totalCartItems: cartSummaryAPIResponse?.data?.totalSKUCount,
    hidePrice,
    addedToCart,
    isMoqError,
    showImageGallery,
    setShowImageGallery,
    hideImages,
    facets,
    onSelectFacet,
    outOfStockFacetValueMap,
  };
};
